import { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import { Form, Input, Radio, Icon, Header } from 'semantic-ui-react';
import toast from 'react-hot-toast';
import ReactGA from 'react-ga4';
import { UnitDecimals } from '~/constants';


import {
  ContractType,
  PricingType,
  Unit,
  InvestorFeedbackBidStatus
} from '~/constants';
import { Button } from '~/components';

import { postProjectBid } from '~/api/projectBids';
import BidConfirmationModal from './modals/BidConfirmationModal';
import budget from '~/utils/displayValues/project/budget';
import { calculateProjectCosts } from '~/utils/calculators';
import { usePermissions } from '~/requests/permissions/usePermissions';
import useArchiveToast from '~/hooks/archive/useArchiveToast';
import InvestorFeedbackModal from './modals/BidFeedback';
import { getProjectBidOverallAmount, normalizeToOverall, normalizeToPerWdc } from '~/utils/calculators/costsFns';
import { sizeKwdc, getProjectStorageSizeKwh } from '~/utils/calculators/system';
import { formatDollars } from '~/utils';
import ITCBidConfirmationModal from './modals/ITCBidConfirmationModal';
import DeclineBidConfirmationModal from './modals/DeclineBidConfirmationModal';
import { ProjectBid } from '~/types/project';
import BudgetTable from '~/features/projectReview/components/BudgetTable';
import { logUserActivity, UserActivityTypes } from '~/api/user';

interface BidFormState {
  amount: number | null;
  unit: keyof typeof Unit;
  investorAcknowledgmentsFlag: boolean;
}

interface BidFormProps {
  project: any;
  id: string;
  fetchProject: () => void;
  projectState: any;
}

const BidForm = ({ project, id, fetchProject, projectState }: BidFormProps) => {
  // Simple state management - no complex form abstractions

  const [formState, setFormState] = useState<BidFormState>(() => {
    let defaultUnit: keyof typeof Unit;

    if (project?.pricingType === PricingType.OFFTAKE && project?.contractType === ContractType.PPA) {
      defaultUnit = Unit.KWH;
    } else if (project?.pricingType === PricingType.OFFTAKE) {
      defaultUnit = Unit.YEAR;
    } else if (project?.pricingType === PricingType.ITC) {
      defaultUnit = Unit.PERCENT;
    } else {
      defaultUnit = Unit.WDC;
    }
    // storage only projects default to per kWh bids
    if (getProjectStorageSizeKwh(project) > 0 && sizeKwdc(project) === 0) {
      defaultUnit = Unit.KWH;
    }

    // Check for existing bid to prepopulate form
    const existingBid = project?.projectBids?.find((bid: any) =>
      bid?.bidStatus === 'ACCEPTED' || bid?.bidStatus === 'PENDING'
    );

    return {
      amount: existingBid?.amount || null,
      unit: existingBid?.unit || defaultUnit,
      investorAcknowledgmentsFlag: true
    };
  });

  const { permissions } = usePermissions();
  const { showArchiveToast } = useArchiveToast();
  const [modalType, setModalType] = useState<null | 'BID_CONFIRMATION' | 'BID_CONFIRMATION_ITC' | 'FEEDBACK_PENDING_BID' | 'FEEDBACK_DECLINED_BID' | 'DECLINE_BID_CONFIRMATION'>(null);
  const [showForm, setShowForm] = useState(true);
  const closeModal = () => setModalType(null);
  const history = useHistory();

  useEffect(() => {
    if (project?.projectBids?.length) {
      setShowForm(false);
    } else {
      setShowForm(true);
    }
  }, [project?.projectBids]);

  // Update form state when existing bid data becomes available or when showing form again
  useEffect(() => {
    if (showForm && project?.projectBids?.length) {
      const existingBid = project?.projectBids?.find((bid: any) =>
        bid?.bidStatus === 'ACCEPTED' || bid?.bidStatus === 'PENDING'
      );

      if (existingBid) {
        setFormState(prev => ({
          ...prev,
          amount: existingBid.amount,
          unit: existingBid.unit
        }));
      }
    }
  }, [showForm, project?.projectBids]);

  const handleBidSubmit = () => {
    ReactGA.event({
      category: 'Bidding',
      action: 'Investor submitted bid',
      label: project ? project.name : undefined
    });
    fetchProject();

    if (!project?.projectBids?.length && !project?.portfolioId) {
      setModalType('FEEDBACK_PENDING_BID');
    } else if (project?.portfolioId) {
      history.push(`/portfolio/${project.portfolioId}/bidding`);
    }

    // Reset form
    setFormState(prev => ({
      ...prev,
      amount: null
    }));
  };

  const { mutateAsync: submitBid, status: submitBidStatus } = useMutation(
    async () => {
      await postProjectBid(formState, id);
      logUserActivity({
        activityType: UserActivityTypes.BID_SUBMITTED,
        projectId: id,
        metadata: {
          amount: formState.amount,
          unit: formState.unit,
        },
      });
      handleBidSubmit();
      toast.success('Thank you for submitting your bid.');
    }
  );

  const projectCosts = calculateProjectCosts(
    project,
    permissions,
    { bid: formState }
  );

  const budgetDisplayValues = budget(project, projectCosts, permissions);

  let fieldLabel: string;
  switch (project?.pricingType) {
    case PricingType.ITC:
      fieldLabel = 'Your bid, as a percentage of total ITC value';
      break;
    case PricingType.OFFTAKE:
      fieldLabel = 'Your offtake bid';
      break;
    case PricingType.ACQUISITION:
      fieldLabel = 'Your bid for ' + (project?.developmentStage === 'OPERATING' ? 'project value' : 'EPC cost plus development fee');
      break;
    case PricingType.DEV_FEE:
      fieldLabel = 'Your development fee bid';
      break;
    default:
      fieldLabel = 'Your bid';
  }

  const showUnits = project?.pricingType === PricingType.ACQUISITION || project?.pricingType === PricingType.DEV_FEE;
  const isPercentInput = formState.unit === Unit.PERCENT;

  // Function to convert bid amount between units
  const convertBidAmount = (amount: number, fromUnit: keyof typeof Unit, toUnit: keyof typeof Unit) => {
    if (!amount || fromUnit === toUnit) return amount;

    const storageSizeKwh = getProjectStorageSizeKwh(project);
    const projectSizeKwdc = sizeKwdc(project);

    // Handle kWh conversions for storage-only projects
    if (storageSizeKwh > 0 && projectSizeKwdc === 0) {
      if (fromUnit === Unit.KWH && toUnit === Unit.TOTAL) {
        return amount * storageSizeKwh;
      } else if (fromUnit === Unit.TOTAL && toUnit === Unit.KWH) {
        return amount / storageSizeKwh;
      }
    }

    // Convert from current unit to total, then to target unit
    let totalAmount: number;
    if (fromUnit === Unit.WDC) {
      totalAmount = normalizeToOverall(projectSizeKwdc, { amount, unit: fromUnit } as ProjectBid);
    } else {
      totalAmount = amount;
    }

    if (toUnit === Unit.WDC) {
      return normalizeToPerWdc(projectSizeKwdc, { amount: totalAmount, unit: Unit.TOTAL } as ProjectBid);
    } else {
      return totalAmount;
    }
  };

  const handleCancelEdit = () => {
    if (project?.projectBids?.length) {
      const existingBid = project?.projectBids?.find((bid: any) =>
        bid?.bidStatus === 'ACCEPTED' || bid?.bidStatus === 'PENDING'
      );

      if (existingBid) {
        setFormState(prev => ({
          ...prev,
          amount: existingBid.amount,
          unit: existingBid.unit
        }));
      }
    }
    setShowForm(false);
  };

  let placeholder = 'Enter bid here';

  return (
    <>

      <div className="bid-form-container">
        {showForm ? (
          <Form className="bid-form">
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <div>
                <Header as="h5" style={{ marginBottom: '8px', fontSize: '14px' }}>{fieldLabel}</Header>

                <Input
                  type={formState.unit === Unit.TOTAL ? 'text' : 'number'}
                  placeholder={placeholder}
                  value={
                    formState.unit === Unit.TOTAL && formState.amount !== null
                      ? formState.amount.toLocaleString('en-US')
                      : formState.amount ?? ''
                  }
                  style={showUnits ? { maxWidth: '16.375rem' } : {}}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormState(prev => {
                      if (value === '') {
                        return {
                          ...prev,
                          amount: null
                        };
                      }

                      // Remove commas for parsing
                      const numericString = value.replace(/,/g, '');
                      const numericValue = Number(numericString);

                      if (Number.isNaN(numericValue)) {
                        return prev;
                      }

                      // Round to 0 decimal places for TOTAL unit, 3 decimal places for others
                      const roundedAmount = prev.unit === Unit.TOTAL
                        ? Math.round(numericValue)
                        : Math.round(numericValue * 1000) / 1000;

                      return {
                        ...prev,
                        amount: roundedAmount
                      };
                    });
                  }}
                  icon='dollar sign'
                  iconPosition='left'
                />

                {isPercentInput && <Icon name="percent" />}

                {showUnits && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '16px', gap: '24px' }}>
                    {getProjectStorageSizeKwh(project) > 0 && sizeKwdc(project) === 0 ? (
                      <Radio
                        label="per kWh"
                        checked={formState.unit === Unit.KWH}
                        onChange={() => {
                          const newAmount = formState.amount ? convertBidAmount(formState.amount, formState.unit, Unit.KWH) : formState.amount;
                          const roundedAmount = newAmount ? Math.round(newAmount * 1000) / 1000 : newAmount;
                          setFormState(prev => ({
                            ...prev,
                            unit: Unit.KWH,
                            amount: roundedAmount
                          }));
                        }}
                      />
                    ) : (
                      <Radio
                        label="per Wdc"
                        checked={formState.unit === Unit.WDC}
                        onChange={() => {
                          const newAmount = formState.amount ? convertBidAmount(formState.amount, formState.unit, Unit.WDC) : formState.amount;
                          const roundedAmount = newAmount ? Math.round(newAmount * 1000) / 1000 : newAmount;
                          setFormState(prev => ({
                            ...prev,
                            unit: Unit.WDC,
                            amount: roundedAmount
                          }));
                        }}
                      />)
                    }

                    <Radio
                      label="Total"
                      checked={formState.unit === Unit.TOTAL}
                      onChange={() => {
                        const newAmount = formState.amount ? convertBidAmount(formState.amount, formState.unit, Unit.TOTAL) : formState.amount;
                        const roundedAmount = newAmount ? Math.round(newAmount) : newAmount;
                        setFormState(prev => ({
                          ...prev,
                          unit: Unit.TOTAL,
                          amount: roundedAmount
                        }));
                      }}
                    />
                  </div>
                )}
              </div>

              {project?.pricingType === PricingType.OFFTAKE && (
                <div style={{ marginLeft: '8px', marginTop: '20px' }}>
                  {project?.contractType === ContractType.PPA ? 'per kWh' : 'per year'}
                </div>
              )}

              {project?.pricingType === PricingType.ITC && (
                <div style={{ width: '100%', marginLeft: 'var(--small)' }}>
                  <strong style={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                    Calculated bid value
                  </strong>
                  <div style={{ lineHeight: '2.625rem' }}>
                    {formState.amount ? formatDollars(getProjectBidOverallAmount(project, formState as ProjectBid), { dec: 0 }) : '-'}
                  </div>
                </div>
              )}
            </div>
          </Form>
        ) : (
          <div>
            <Header as="h5" style={{ marginBottom: '8px', fontSize: '14px' }}>{fieldLabel}</Header>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
              <div style={{ fontSize: '16px', fontWeight: 600, border: '1px solid #ddd', background: '#f1f1f1', padding: '8px 16px', borderRadius: '4px', minWidth: '120px', textAlign: 'left' }}>
                {formState.amount ? (
                  <>
                    {isPercentInput ? (
                      <>
                        {formState.amount.toFixed(2)}%
                      </>
                    ) : (
                      <>
                        {formatDollars(formState.amount, {
                          dec: formState.unit === Unit.WDC ? UnitDecimals.WDC :
                            formState.unit === Unit.KWH ? UnitDecimals.KWH :
                              UnitDecimals.TOTAL
                        })}
                      </>
                    )}

                  </>
                ) : (
                  <>-</>
                )}
              </div>
              <span style={{ marginLeft: '2px', padding: '0' }}>
                {
                  formState.unit === Unit.WDC ? '/Wdc' :
                    formState.unit === Unit.KWH ? '/kWh' :
                      formState.unit === Unit.YEAR ? '/year' :
                        'total'
                }
              </span>
            </div>
          </div>
        )}


        <div className="bid-form-actions">
          {showForm ? (
            <div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-start' }}>
                <div>
                  <Button
                    primary
                    onClick={() => {
                      if (showArchiveToast()) {
                        return;
                      } else if (project?.pricingType === PricingType.ITC) {
                        setModalType('BID_CONFIRMATION_ITC');
                      } else {
                        setModalType('BID_CONFIRMATION');
                      }
                    }}
                    disabled={!formState.amount || submitBidStatus === 'loading'}
                  >
                    {project?.projectBids?.length ? 'Update bid' : 'Submit bid'}
                  </Button>

                  {project?.projectBids?.length ? (
                    <Button
                      primary
                      outlined
                      onClick={() => handleCancelEdit()}
                      style={{ marginLeft: '8px' }}
                    >
                      Cancel edit
                    </Button>
                  ) : null}
                </div>

                <Button
                  primary
                  outlined
                  onClick={() => {
                    if (showArchiveToast()) {
                      return;
                    } else if (project?.pricingType === PricingType.ITC) {
                      setModalType('DECLINE_BID_CONFIRMATION');
                    } else {
                      setModalType('FEEDBACK_DECLINED_BID');
                    }
                  }}
                >
                  Decline to bid
                </Button>
              </div>
            </div>
          ) : (
            <Button
              primary
              outlined
              onClick={() => {
                setShowForm(true);
              }}
            >
              Change your bid
            </Button>
          )}
        </div>
      </div>
      {project.pricingType !== PricingType.OFFTAKE ? (
        <div style={{ marginTop: '28px' }}>
          <BudgetTable
            project={project}
            formState={formState}
          />
        </div>
      ) : null}


      {modalType === 'BID_CONFIRMATION' &&
        <BidConfirmationModal
          submitBid={submitBid}
          submitBidStatus={submitBidStatus}
          budgetDisplayValues={budgetDisplayValues}
          isEpcOperating={projectState?.isEpcOperating}
          modalPricingType={project?.pricingType as keyof typeof PricingType}
          closeModal={closeModal}
          project={project}
          formState={formState}
        />
      }
      {modalType === 'BID_CONFIRMATION_ITC' &&
        <ITCBidConfirmationModal
          submitBid={submitBid}
          submitBidStatus={submitBidStatus}
          projectCosts={projectCosts}
          closeModal={closeModal}
        />
      }
      {modalType === 'FEEDBACK_PENDING_BID' &&
        <InvestorFeedbackModal
          bidStatus={InvestorFeedbackBidStatus.BID_PENDING}
          closeModal={closeModal}
        />
      }
      {modalType === 'FEEDBACK_DECLINED_BID' &&
        <InvestorFeedbackModal
          bidStatus={InvestorFeedbackBidStatus.BID_DECLINED}
          closeModal={closeModal}
          project={project}
        />
      }
      {modalType === 'DECLINE_BID_CONFIRMATION' &&
        <DeclineBidConfirmationModal closeModal={closeModal} />
      }
    </>
  );
};

interface BidFormWrapperProps {
  project: any;
  id: string;
  fetchProject: () => void;
  projectState: any;
}

export default function BidFormWrapper({ project, id, fetchProject, projectState }: BidFormWrapperProps) {
  return (
    <BidForm
      project={project}
      id={id}
      fetchProject={fetchProject}
      projectState={projectState}
    />
  );
}